/**
 * @file Live tuning layer over enemy archetypes (issue #124).
 *
 * `ENEMIES` stays the shipped balance table. This is the editable copy on top of
 * it: sparse numeric overrides per `enemyKey.path`, merged back into a read-only
 * view, which is what the debug window edits and what `createEnemy` reads.
 *
 * It shares its mechanism with {@link ./WeaponTuning.js}, and literally so: both
 * are {@link ./keyedTuning.js createKeyedTuningLayer} over a frozen table, so the
 * two tabs of the debug window cannot drift apart. What is enemy-specific is
 * here — the editing rules, and what is left out. Strings and booleans
 * (`behavior`, `weapon`, `starterState`, the combat-pattern names) fall out of the
 * type test on their own; a packed `0xRRGGBB` colour does not, and is excluded by
 * name for the same reason it is everywhere else here: it is a palette reference,
 * not a balance knob.
 *
 * Like every other tuning layer here, an edit reaches enemies *spawned from now
 * on*: `createEnemy` bakes these numbers onto components, so what is already
 * walking around the level keeps the values it was born with.
 */

import { ENEMIES } from './Enemies.js';
import { createKeyedTuningLayer } from './keyedTuning.js';

/** `localStorage` key holding the override map. Never shared with weapons. */
export const ENEMY_TUNING_STORAGE_KEY = 'spin.enemyTuning.v1';

/**
 * Per-field editing rules, keyed by full dotted path so nested blocks get their
 * own labels. A field missing from the table still renders — it falls back to a
 * humanised key and conservative numeric defaults — so a new archetype field
 * appears in the window without an edit here.
 * @type {Readonly<Record<string, { label: string, step: number, min: number, integer?: boolean }>>}
 */
const ENEMY_META = Object.freeze({
  radius: { label: 'Radius', step: 0.01, min: 0.1 },
  height: { label: 'Height', step: 0.05, min: 0.1 },
  maxHealth: { label: 'Max health', step: 1, min: 1 },
  speed: { label: 'Speed', step: 0.1, min: 0 },
  acceleration: { label: 'Acceleration', step: 1, min: 0 },
  damage: { label: 'Contact damage', step: 1, min: 0 },
  attackRate: { label: 'Attack rate', step: 0.05, min: 0 },
  attackRange: { label: 'Attack range', step: 0.1, min: 0 },
  seekRange: { label: 'Seek range', step: 1, min: 0 },
  preferredRange: { label: 'Preferred range', step: 0.5, min: 0 },
  separationRadius: { label: 'Separation radius', step: 0.1, min: 0 },
  separationStrength: { label: 'Separation strength', step: 0.5, min: 0 },
  scoreValue: { label: 'Score', step: 1, min: 0, integer: true },
  dropBonus: { label: 'Drop bonus', step: 0.01, min: 0 },
  alertRadius: { label: 'Alert radius', step: 0.5, min: 0 },
  despawnSeconds: { label: 'Despawn (s)', step: 0.5, min: 0 },
  'detection.fieldOfViewDegrees': { label: 'FOV (deg)', step: 1, min: 0 },
  'detection.range': { label: 'Vision range', step: 0.5, min: 0 },
  'detection.awarenessSeconds': { label: 'Awareness (s)', step: 0.05, min: 0 },
  'detection.memorySeconds': { label: 'Memory (s)', step: 0.1, min: 0 },
  'idle.walkSpeed': { label: 'Idle walk speed', step: 0.1, min: 0 },
  'idle.speedScale': { label: 'Idle speed ×', step: 0.05, min: 0 },
  'idle.radius': { label: 'Idle radius', step: 0.1, min: 0 },
  'idle.pauseSeconds': { label: 'Idle pause (s)', step: 0.1, min: 0 },
  'aggro.speedScale': { label: 'Aggro speed ×', step: 0.05, min: 0 },
  'ranged.idle_walk_speed': { label: 'Idle walk speed', step: 0.1, min: 0 },
  'ranged.aggro_walk_speed': { label: 'Aggro walk speed', step: 0.1, min: 0 },
  'aggro.chargeSeconds': { label: 'Charge (s)', step: 0.05, min: 0 },
  'explosiveTrigger.triggerDistance': { label: 'Trigger distance', step: 0.1, min: 0 },
  'explosiveTrigger.fuseSeconds': { label: 'Fuse (s)', step: 0.05, min: 0 },
  'explosiveTrigger.deathFuseSeconds': { label: 'Death fuse (s)', step: 0.05, min: 0 },
  'explosiveTrigger.damage': { label: 'Blast damage', step: 1, min: 0 },
  'explosiveTrigger.radius': { label: 'Blast radius', step: 0.1, min: 0 },
  'explosiveTrigger.minimumDamage': { label: 'Rim damage', step: 1, min: 0 },
  'explosiveTrigger.force': { label: 'Blast force', step: 0.5, min: 0 },
  'explosive.radius': { label: 'Death blast radius', step: 0.1, min: 0 },
  'explosive.damage': { label: 'Death blast damage', step: 1, min: 0 }
});

const DEFAULT_META = Object.freeze({ label: '', step: 0.1, min: 0 });

/**
 * A packed `0xRRGGBB` colour is a number the type test would otherwise let
 * through; masks and layers are covered for the same reason the weapon layer
 * covers them, so the two tables stay judged by one rule.
 *
 * @param {string} _path Full dotted path; unused, the leaf name is enough here.
 * @param {string} key The leaf's own key.
 * @returns {boolean}
 */
function isSkippedNumeric(_path, key) {
  return /color|mask|layer/i.test(key);
}

const layer = createKeyedTuningLayer({
  table: ENEMIES,
  storageKey: ENEMY_TUNING_STORAGE_KEY,
  meta: ENEMY_META,
  defaultMeta: DEFAULT_META,
  isSkipped: isSkippedNumeric,
  exportLabel: 'enemies'
});

export const setEnemyTuningStorage = layer.setStorage;
/** @returns {typeof ENEMIES} `ENEMIES` with the current overrides applied. */
export const getEnemyTuning = () => /** @type {typeof ENEMIES} */ (layer.getTuning());
export const getEnemyArchetype = layer.getEntry;
export const enemyTuningPaths = layer.tuningPaths;
export const getEnemyFieldMeta = layer.getFieldMeta;
export const getEnemyDefaultValue = layer.getDefaultValue;
export const setEnemyValue = layer.setValue;
export const getEnemyOverrides = layer.getOverrides;
export const hasEnemyOverrides = layer.hasOverrides;
export const isEnemyValueOverridden = layer.isValueOverridden;
export const applyEnemyOverrides = layer.applyOverrides;
export const saveEnemyTuning = layer.save;
export const loadEnemyTuning = layer.load;
export const resetEnemyTuning = layer.reset;
export const exportEnemyTuning = () => layer.export();
export const onEnemyTuningChanged = layer.onChanged;
export const inspectEnemyTuning = layer.inspect;
